import * as React from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import { AsciiEffect } from 'three-stdlib'

/**
 * Local copy of drei's AsciiRenderer with a fix for the race condition where
 * useFrame fires before useEffect has called setSize(), causing getImageData
 * to receive undefined dimensions.
 */
export function AsciiRenderer({
  renderIndex = 1,
  bgColor = 'black',
  fgColor = 'white',
  characters = ' .:-+*=%@#',
  invert = true,
  color = false,
  resolution = 0.15,
}) {
  const { size, gl, scene, camera } = useThree()
  const initialized = React.useRef(false)

  const effect = React.useMemo(() => {
    const effect = new AsciiEffect(gl, characters, {
      invert,
      color,
      resolution,
    })
    effect.domElement.style.position = 'absolute'
    effect.domElement.style.top = '0px'
    effect.domElement.style.left = '0px'
    effect.domElement.style.pointerEvents = 'none'
    return effect
  }, [characters, invert, color, resolution])

  // Styling
  React.useLayoutEffect(() => {
    effect.domElement.style.color = fgColor
    effect.domElement.style.backgroundColor = bgColor
  }, [fgColor, bgColor])

  // Append on mount, remove on unmount
  React.useEffect(() => {
    gl.domElement.style.opacity = '0'
    gl.domElement.parentNode.appendChild(effect.domElement)
    return () => {
      gl.domElement.style.opacity = '1'
      gl.domElement.parentNode.removeChild(effect.domElement)
      initialized.current = false
    }
  }, [effect])

  // Set size — mark as initialized so useFrame knows it's safe to render
  React.useEffect(() => {
    if (size.width > 0 && size.height > 0) {
      effect.setSize(size.width, size.height)
      initialized.current = true
    }
  }, [effect, size])

  // Only render once setSize has been called at least once
  useFrame(() => {
    if (initialized.current) {
      effect.render(scene, camera)
    }
  }, renderIndex)

  return null
}
